/**
 * HTTP请求工具
 * 基于axios封装，统一处理域名、请求头、错误等
 */
import axios from 'axios'
import config from '@/config'
import Vue from 'vue'
import router from '@/router'

let isHandling415 = false
let isHandling412 = false

function handleUnauthorized(message = '登录已失效，请重新登录') {
  localStorage.removeItem('token')
  localStorage.removeItem('userinfo')
  sessionStorage.removeItem('token')
  Vue.prototype.$message && Vue.prototype.$message.error(message)
  window.location.replace('/')
}

// 创建axios实例
const service = axios.create({
  baseURL: config.baseURL, // 基础URL
  timeout: config.timeout || 10000, // 请求超时时间
  headers: {
    'Content-Type': 'application/json',
  }
})

/**
 * 请求拦截器 - 在发送请求前处理
 */
service.interceptors.request.use(
  config => {
    // 添加token等认证信息（如需要可取消注释）
    const token = localStorage.getItem('token') || sessionStorage.getItem('token')
    if (token) {
       config.headers['authorization'] = token
    }
    
    return config
  },
  error => {
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

/**
 * 响应拦截器 - 处理响应数据
 */
service.interceptors.response.use(
  response => {
    const res = response.data
    console.log(res,)
    const status = Number(res && res.status)
    const code = Number(res && res.code)
    const isSuccess =
      status === 200 ||
      code === 200 ||
      code === 0 ||
      res?.success === true
    const isUnauthorized = status === 401 || code === 401
    // 根据后端返回的数据结构处理
    // 如果后端返回格式为 { code: 200, data: {}, message: '' }
    if (res.status !== undefined || res.code !== undefined || res?.success !== undefined) {
      if (isSuccess) {
		   console.log(res)
        // 兼容旧代码：保持 axios 响应结构，业务数据仍在 res.data
        return Promise.resolve({
          ...response,
          data: res
        })
      } else if(isUnauthorized){
		  
		  handleUnauthorized(res.message || '登录已失效，请重新登录')
		  const error = new Error(res.message || '登录已失效，请重新登录')
		  error.status = 401
		  return Promise.reject(error)
		  
	  } else if (status === 415) {
        Vue.prototype.$message.error(res.msg || res.message || '请完善信息')
        if (!isHandling415 && Vue.prototype.$confirm) {
          isHandling415 = true
          Vue.prototype.$confirm('是否立即完善服务信息？', '提示', { type: 'warning' })
            .then(() => router.push('/serviceHome/UserInfo'))
            .catch(() => {})
            .finally(() => {
              isHandling415 = false
            })
        }
        return Promise.resolve({
          ...response,
          data: res
        })
	  }else if(status === 412){
			if (!isHandling412 && Vue.prototype.$confirm) {
			  isHandling412 = true
			  Vue.prototype.$confirm('请立即选择身份？', '提示', { type: 'warning' })
				.then(() => router.push('/'))
				.catch(() => {})
				.finally(() => {
				  isHandling412 = false
				})
			}
			return Promise.resolve({
			  ...response,
			  data: res
			})
      }else{
		 // 业务错误处理
		 Vue.prototype.$message.error(res.msg || '请求失败')
		 // 兼容现有页面：业务失败也返回响应，由调用方按 status/code 自行处理
		 return Promise.resolve({
		   ...response,
		   data: res
		 }) 
	  }
    }
    
    // 如果后端直接返回数据，包装成统一格式
    return Promise.resolve({
      ...response,
      data: {
        status: 200,
        data: res,
        message: 'success'
      }
    })
  },
  error => {
    
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

/**
 * 通用请求方法
 * @param {string} url - 请求地址（相对路径）
 * @param {object} data - 请求参数（GET请求时为params，POST等为data）
 * @param {object} config - axios配置选项
 * @param {string} method - 请求方法 GET/POST/PUT/DELETE
 */
export function $request(url, data = {}, config = {}, method = 'GET') {
  // 统一处理URL，确保以/开头（如果baseURL为空或相对路径）
  // 如果URL已经是完整地址（http/https开头），则不处理
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    // 确保URL以/开头
    if (!url.startsWith('/')) {
      url = '/' + url
    }
  }
  
  const requestConfig = {
    url,
    method: method.toUpperCase(),
    ...config
  }
  
  // 根据请求方法设置参数
  if (method.toUpperCase() === 'GET' || method.toUpperCase() === 'DELETE') {
    requestConfig.params = data
  } else {
    requestConfig.data = data
  }
  
  return service(requestConfig)
}

/**
 * GET请求
 */
export function get(url, params = {}, options = {}) {
  return service({
    url,
    method: 'GET',
    params,
    ...options
  })
}

/**
 * POST请求
 */
export function post(url, data = {}, options = {}) {
  return service({
    url,
    method: 'POST',
    data,
    ...options
  })
}

/**
 * PUT请求
 */
export function put(url, data = {}, options = {}) {
  return service({
    url,
    method: 'PUT',
    data,
    ...options
  })
}

/**
 * DELETE请求
 */
export function del(url, params = {}, options = {}) {
  return service({
    url,
    method: 'DELETE',
    params,
    ...options
  })
}

// 导出axios实例和请求方法
export default service
export { service }


